import * as React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";

/* ------------------------------------------------------------------
   Reema brand chart primitives.
   Rule 47: charts are allowed, but every color, font, and stroke must
   come from brand tokens. No drop shadows, no gradients, no default
   recharts grays. Axis labels in Poppins; numeric labels in Lora.
   ------------------------------------------------------------------ */

// Exported so other report charts (HarpEnrollment, HomeVisits, GapClosures)
// can reuse the same brand-safe font/color tokens.
export const FONT_BODY = "var(--reema-font-body)";
export const FONT_DISPLAY = "var(--reema-font-display)";
export const TEXT_PRIMARY = "var(--reema-midnight-100)";
export const TEXT_SECONDARY = "var(--reema-midnight-75)";

const tickStyle = {
  fontFamily: FONT_BODY,
  fontSize: 12,
  fontWeight: 600,
  fill: TEXT_SECONDARY,
};

/* Horizontal bar chart — population by tier, needs by category, etc. */
export function BrandBarChart({
  data,
  height = 320,
  dataKey = "value",
  categoryKey = "label",
  color = "var(--reema-coral-100)",
  formatValue = (v: number) => v.toLocaleString(),
  onDark = false,
}: {
  data: Array<Record<string, any>>;
  height?: number;
  dataKey?: string;
  categoryKey?: string;
  color?: string;
  formatValue?: (v: number) => string;
  onDark?: boolean;
}) {
  const tick = { ...tickStyle, fill: onDark ? "rgba(255,255,255,0.78)" : TEXT_SECONDARY };
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 64, bottom: 8, left: 8 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey={categoryKey}
            axisLine={false}
            tickLine={false}
            tick={tick}
            width={160}
          />
          <Bar dataKey={dataKey} fill={color} radius={[12, 12, 12, 12]} barSize={28}>
            <LabelList
              dataKey={dataKey}
              position="right"
              formatter={formatValue}
              style={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: 16,
                fill: onDark ? "var(--reema-white)" : TEXT_PRIMARY,
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* Stacked bar — identified vs fulfilled, etc. */
export function BrandStackedBarChart({
  data,
  height = 340,
  keys,
  categoryKey = "label",
  onDark = false,
}: {
  data: Array<Record<string, any>>;
  height?: number;
  keys: Array<{ key: string; color: string; label: string }>;
  categoryKey?: string;
  onDark?: boolean;
}) {
  const tick = { ...tickStyle, fill: onDark ? "rgba(255,255,255,0.78)" : TEXT_SECONDARY };
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey={categoryKey}
            axisLine={false}
            tickLine={false}
            tick={tick}
            width={200}
          />
          <Tooltip cursor={{ fill: "transparent" }} contentStyle={{
            border: "none",
            background: "var(--reema-white)",
            fontFamily: FONT_BODY,
            fontSize: 12,
            borderRadius: 12,
            boxShadow: "none",
            color: TEXT_PRIMARY,
          }} />
          {keys.map((k, idx) => (
            <Bar
              key={k.key}
              dataKey={k.key}
              stackId="a"
              fill={k.color}
              radius={idx === 0 ? [12, 0, 0, 12] : idx === keys.length - 1 ? [0, 12, 12, 0] : 0}
              barSize={28}
              name={k.label}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* Line chart — outcomes over time (PHQ-9, GAD-7). */
export function BrandLineChart({
  data,
  series,
  height = 320,
  xKey = "label",
  yDomain,
  onDark = false,
  showLegend = true,
  margin,
  yAxisWidth = 36,
}: {
  data: Array<Record<string, string | number>>;
  series: Array<{ key: string; color: string; label: string }>;
  height?: number;
  xKey?: string;
  yDomain?: [number, number];
  onDark?: boolean;
  /** When true, renders a series key below the chart (default: on). */
  showLegend?: boolean;
  /** Optional Recharts margin override. */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Y-axis width in px (default 36). */
  yAxisWidth?: number;
}) {
  const tick = { ...tickStyle, fill: onDark ? "rgba(255,255,255,0.78)" : TEXT_SECONDARY };
  const chartMargin = {
    top: margin?.top ?? 12,
    right: margin?.right ?? 8,
    bottom: margin?.bottom ?? 8,
    left: margin?.left ?? 0,
  };

  return (
    <div className="brand-line-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={chartMargin}>
            <CartesianGrid stroke={onDark ? "rgba(255,255,255,0.08)" : "var(--reema-midnight-10)"} vertical={false} />
            <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={tick} />
            <YAxis axisLine={false} tickLine={false} tick={tick} domain={yDomain} width={yAxisWidth} />
            <Tooltip cursor={{ stroke: "var(--reema-midnight-25)", strokeWidth: 1 }} contentStyle={{
              border: "none",
              background: "var(--reema-white)",
              fontFamily: FONT_BODY,
              fontSize: 12,
              borderRadius: 12,
              boxShadow: "none",
              color: TEXT_PRIMARY,
            }} />
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={3}
                dot={{ r: 5, fill: s.color, strokeWidth: 0 }}
                activeDot={{ r: 7, fill: s.color, strokeWidth: 0 }}
                name={s.label}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {showLegend ? (
        <BrandLegend
          items={series.map(s => ({ label: s.label, color: s.color }))}
          onDark={onDark}
        />
      ) : null}
    </div>
  );
}

/* Donut — share of a population (e.g., 1 in 4). */
export function BrandDonut({
  value,
  total,
  color = "var(--reema-coral-100)",
  trackColor = "var(--reema-sand-100)",
  centerLabel,
  centerSubLabel,
  size = 240,
}: {
  value: number;
  total: number;
  color?: string;
  trackColor?: string;
  centerLabel?: string;
  centerSubLabel?: string;
  size?: number;
}) {
  const data = [
    { name: "value", v: value },
    { name: "rest", v: Math.max(total - value, 0) },
  ];
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="v"
            innerRadius="72%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive={false}
          >
            <Cell fill={color} />
            <Cell fill={trackColor} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        {centerLabel && (
          <div
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 700,
              fontSize: 48,
              lineHeight: 1,
              color: TEXT_PRIMARY,
            }}
          >
            {centerLabel}
          </div>
        )}
        {centerSubLabel && (
          <div
            style={{
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: TEXT_SECONDARY,
              marginTop: 8,
              maxWidth: size * 0.7,
            }}
          >
            {centerSubLabel}
          </div>
        )}
      </div>
    </div>
  );
}

/* Funnel as horizontal stacked bars — eligible → outreached → engaged. */
export function BrandFunnel({
  steps,
  height = 280,
}: {
  steps: Array<{ label: string; value: number; color: string }>;
  height?: number;
}) {
  const max = Math.max(...steps.map((s) => s.value));
  return (
    <div style={{ width: "100%", display: "grid", gap: 16, minHeight: height }}>
      {steps.map((s) => {
        const pct = (s.value / max) * 100;
        return (
          <div key={s.label} style={{ display: "grid", gridTemplateColumns: "180px 1fr 120px", gap: 16, alignItems: "center" }}>
            <div style={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13, color: TEXT_SECONDARY }}>
              {s.label}
            </div>
            <div style={{ position: "relative", height: 40, background: "transparent" }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: s.color,
                  borderRadius: 9999,
                  transition: "width 400ms ease",
                }}
              />
            </div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 22, color: TEXT_PRIMARY }}>
              {s.value.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Legend chip row — used under stacked / multi-series charts. */
export function BrandLegend({
  items,
  onDark = false,
  fontWeight = 400,
}: {
  items: Array<{ label: string; color: string }>;
  onDark?: boolean;
  /** Legend label weight (default normal). */
  fontWeight?: number;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 12, justifyContent: 'center' }}>
      {items.map((it) => (
        <div key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 9999, background: it.color, display: 'inline-block' }} />
          <span style={{
            fontFamily: FONT_BODY,
            fontWeight,
            fontSize: 13,
            color: onDark ? 'rgba(255,255,255,0.85)' : TEXT_SECONDARY,
          }}>
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
}
